import {
  Box,
  Flex,
  FormControl,
  IconButton,
  Input,
  Switch,
  Text,
} from '@chakra-ui/react';
import { MatrixNode } from '@texas/api/endpoints/matrixConfigurationApi';
import { CreateArticleNodeValue } from '@texas/api/endpoints/articlesApi';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { Icons } from '@texas/components/shared/Icons';
import { WarningProps } from '@texas/components/shared/WarningComponent';
import { createArticleStyles as styles } from '@texas/resources/theme/styles/entityWorkspace';
import { ArticleFieldWarning } from './ArticleFieldWarning';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  node: MatrixNode;
  values: CreateArticleNodeValue[];
  onChange: (values: CreateArticleNodeValue[]) => void;
  max: number;
  warningProps: WarningProps;
  isColorAxis: boolean;
  colorAxisTargetId?: string;
  onColorAxisChange: (checked: boolean) => void;
}

export function CreateArticleMatrix({
  node,
  values,
  onChange,
  max,
  warningProps,
  isColorAxis,
  colorAxisTargetId,
  onColorAxisChange,
}: Props) {
  const { t } = useTranslation();
  const pickerId = useId();
  const available = node.matrixNodeValues.filter((v) => !v.archived);
  return (
    <Box sx={styles.matrixContainer} id={warningProps.targetId}>
      <Flex
        id={colorAxisTargetId}
        align="center"
        justify="space-between"
        gap={3}
        wrap="wrap"
      >
        <TexasFormLabel htmlFor={pickerId} fontWeight="normal">
          <Text as="span" fontWeight="bold">
            {isColorAxis ? t('general.color') : t('general.size')}:
          </Text>{' '}
          {node.name} ({values.length}/{max})
        </TexasFormLabel>

        <Switch
          size="sm"
          isChecked={isColorAxis}
          aria-label={`${t('article.create.colorAxis')} · ${node.name}`}
          onChange={(event) => onColorAxisChange(event.target.checked)}
        >
          {t('article.create.colorAxis')}
        </Switch>
      </Flex>
      {values.length > 0 && (
        <Flex direction="column" gap={1}>
          {values.map((value) => {
            const nodeValue = available.find((v) => v.id === value.id);
            return (
              <Flex key={value.id} align="center" gap={2} minW={0}>
                <Text
                  w="64px"
                  flexShrink={0}
                  fontSize="sm"
                  overflowWrap="anywhere"
                >
                  {nodeValue?.identifier ?? '—'}
                </Text>
                <Input
                  variant="texas"
                  aria-label={`${nodeValue?.identifier ?? ''} ${
                    nodeValue?.name ?? value.name
                  }`.trim()}
                  placeholder={nodeValue?.name}
                  value={value.name}
                  minW={0}
                  flex={1}
                  maxLength={20}
                  onChange={(e) =>
                    onChange(
                      values.map((v) =>
                        v.id === value.id ? { ...v, name: e.target.value } : v,
                      ),
                    )
                  }
                />
                <IconButton
                  aria-label={`${t('general.remove')} ${
                    nodeValue?.name ?? value.name
                  }`}
                  icon={<Icons.TrashOutline />}
                  variant="ghost"
                  size="sm"
                  flexShrink={0}
                  onClick={() =>
                    onChange(values.filter((v) => v.id !== value.id))
                  }
                />
              </Flex>
            );
          })}
        </Flex>
      )}
      <FormControl isRequired={true} isInvalid={!!warningProps.warning}>
        <TexasSelect
          inputId={pickerId}
          aria-label={node.name}
          isMulti={true}
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          isSearchable={true}
          isClearable={false}
          backspaceRemovesValue={false}
          controlShouldRenderValue={false}
          hideSelectedOptions={true}
          options={available.map((v) => ({
            value: v.id,
            label: `${v.identifier} · ${v.name}`,
          }))}
          value={values.map((v) => ({
            value: v.id,
            label: available.find((a) => a.id === v.id)?.name ?? v.name,
          }))}
          onChange={(options) =>
            onChange(
              options.slice(0, max).map(
                (o) =>
                  values.find((v) => v.id === o.value) ?? {
                    id: Number(o.value),
                    name: '',
                  },
              ),
            )
          }
        />
        <ArticleFieldWarning {...warningProps} />
      </FormControl>
    </Box>
  );
}
